import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type User } from '@prisma/client'
import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link } from '@remix-run/react'
import {
	Equal,
	ExternalLinkIcon,
	Hexagon,
	InfoIcon,
	PlayCircleIcon,
	TreeDeciduous,
} from 'lucide-react'
import { GiftDemoModal } from '#app/components/dialogs/gift-dialog.js'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion'
import { Button } from '#app/components/ui/button'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip'
import { getUserById } from '#app/models/user/get-user'
import { authenticator } from '#app/utils/auth.server'
import { isEarthDay } from '#app/utils/is-earth-day.js'
import { cn } from '#app/utils/misc'

type LoaderData = {
	user: User | null
}

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/digital-gift' }],
}

export async function loader({ request }: LoaderFunctionArgs) {
	const session = await authenticator.isAuthenticated(request)

	if (!session) return json<LoaderData>({ user: null })

	const user = await getUserById(session.id)

	return json<LoaderData>({ user })
}

export const meta: MetaFunction = () => [
	{ title: `Gift a piece of the world & plant trees for $2 | Erda` },
	{
		name: 'description',
		content: `Send someone a unique sentimental gift and plant two trees, aiding in the fight against hunger, poverty, and environmental destruction.`,
	},
	{
		name: 'keywords',
		content:
			'digital, gift, meaningful, sentimental, unique, instant, piece, world, map, plant, tree, helps, hunger, poverty, environmental, green, sustainability',
	},
	{
		name: 'og:title',
		content: `Gift a meaningful piece of the world & plant trees for $2 | Erda`,
	},
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/digital-gift.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Send someone a unique sentimental gift and plant two trees, aiding in the fight against hunger, poverty, and environmental destruction.`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/digital-gift.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{
		name: 'twitter:title',
		content: `Gift a meaningful piece of the world & plant trees for $2 | Erda`,
	},
	{
		name: 'twitter:description',
		content: `Send someone a unique sentimental gift and plant two trees, aiding in the fight against hunger, poverty, and environmental destruction.`,
	},
]

export default function DigitalGift() {
	return (
		<>
			<div>
				<h1 className="mx-auto w-[280px] text-center text-sm font-bold tracking-tight md:w-[360px] md:text-lg">
					Gift someone a digital Plot — each a unique piece of the world for
					just $2 per year.
				</h1>

				<div className="my-1 flex items-center justify-center gap-2 rounded-lg p-4">
					<Hexagon
						className={cn('h-8 w-8 fill-purple-400/50 stroke-purple-500')}
						strokeWidth={1.5}
					/>
					<Equal className="h-6 w-6" />
					<div className="flex items-center justify-center">
						<TreeDeciduous
							className="h-8 w-8 fill-green-400/50 stroke-green-500"
							strokeWidth={1.5}
						/>
						<TreeDeciduous
							className="h-8 w-8 fill-green-400/50 stroke-green-500"
							strokeWidth={1.5}
						/>
					</div>
				</div>
				<p className="mx-auto w-[280px] text-center text-sm tracking-tight md:w-[350px] md:text-base">
					Every purchase and renewal{' '}
					<span className="text-sm font-bold md:text-lg">
						plants{' '}
						{isEarthDay() ? (
							<>
								<span className="text-muted-foreground line-through">two</span>{' '}
								three trees
							</>
						) : (
							'two trees'
						)}
					</span>
					, helping break the cycle of hunger, poverty, and environmental
					destruction.{' '}
					<a
						href="https://trees.org"
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1 underline"
					>
						How? <ExternalLinkIcon className="inline h-4 w-4" />
					</a>
				</p>
			</div>

			<hr className="mt-2" />

			<div className="mt-4 flex-1 space-y-2">
				<div className="flex items-baseline justify-between">
					<h2 className="font-bold">A unique &amp; sentimental gift</h2>
				</div>
				<p className="text-sm">
					Do you know a place that's meaningful to someone? A place they've
					been, aspire to visit, wish to protect, or share with others?
				</p>
				<p className="text-sm">
					Gift them a digital Plot on the map, a unique piece of the world for
					them to own forever.
				</p>
				<p className="text-sm">
					In doing so, you'll also be planting{' '}
					{isEarthDay() ? (
						<>
							<span className="text-muted-foreground line-through">two</span>{' '}
							three trees
						</>
					) : (
						'two trees'
					)}
					, helping to break the cycle of hunger, poverty, and environmental
					destruction.
				</p>
			</div>

			<div className="mt-4 flex-1 space-y-2">
				<div className="flex items-center justify-between">
					<h2 className="font-bold">Gifting is simple</h2>
					<GiftDemoModal
						trigger={
							<Button size="sm" className="my-3">
								<PlayCircleIcon className="h-4 w-4" /> Watch demo
							</Button>
						}
					/>
				</div>
				<div className="flex gap-2 text-sm">
					<span className="font-bold text-muted-foreground">1.</span>{' '}
					<div className="flex flex-col gap-2">
						<p>
							Click on the map to select one or more Plots, indicated by a
							dashed black outline.{' '}
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger>
										<div className="flex items-center justify-center gap-1 text-center text-xs text-muted-foreground">
											<InfoIcon className="h-3 w-3" />
											Can't select a Plot?
										</div>
									</TooltipTrigger>
									<TooltipContent className="space-y-1">
										<p>
											Try zooming in more if you can't select a Plot when
											clicking or tapping on the map.
										</p>
										<p>Purple Plots are already owned by someone else.</p>
									</TooltipContent>{' '}
								</Tooltip>
							</TooltipProvider>{' '}
						</p>
						<p>
							When ready, click the{' '}
							<Hexagon
								className={cn('-mt-px inline-flex h-4 w-4 fill-black/10')}
								strokeDasharray={3}
								strokeWidth={2}
							/>{' '}
							button at the top right to checkout.{' '}
						</p>
					</div>
				</div>
				<div className="flex gap-2 text-sm">
					<span className="font-bold text-muted-foreground">2.</span>{' '}
					<p>
						During checkout, provide the recipient's email address, name & an
						optional message.
					</p>
				</div>
				<div className="flex gap-2 text-sm">
					<span className="font-bold text-muted-foreground">3.</span>{' '}
					<p>
						After checkout, customise the Plot(s) and schedule an email to be
						sent to the recipient at a date and time of your choosing.
					</p>
				</div>
				<div className="flex gap-2 text-sm">
					<span className="font-bold text-muted-foreground">4.</span>{' '}
					<p>
						The recipient will receive an email with a link to claim their
						Plot(s)
					</p>
				</div>
				<div className="flex gap-2 text-sm">
					<span className="font-bold text-muted-foreground">5.</span>{' '}
					<p>
						Done! The recipient can now view their Plot(s) on the map and
						customise them as they wish.
					</p>
				</div>
			</div>

			<div className="mt-4 flex-1">
				<h2 className="font-bold">FAQs</h2>
				<Accordion type="single" collapsible className="w-full">
					<AccordionItem data-vaul-no-drag value="item-1">
						<AccordionTrigger data-vaul-no-drag>
							<h3 data-vaul-no-drag className="text-sm">
								What is a Plot?
							</h3>
						</AccordionTrigger>
						<AccordionContent>
							<p>A Plot is a digital piece of land on the map.</p>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem data-vaul-no-drag value="item-3">
						<AccordionTrigger data-vaul-no-drag>
							<h3 data-vaul-no-drag className="text-sm">
								Why can't I select a Plot?
							</h3>
						</AccordionTrigger>
						<AccordionContent>
							<p>
								Ensure you're zoomed in enough to see the Plots. Purple Plots
								are already owned.
							</p>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem data-vaul-no-drag value="item-7">
						<AccordionTrigger data-vaul-no-drag>
							<h3 data-vaul-no-drag className="text-sm">
								Does the recipient own the Plot forever?
							</h3>
						</AccordionTrigger>
						<AccordionContent className="flex flex-col gap-2">
							<p>
								The Plot will remain the recipients for as long as you keep
								renewing it for $2 per year. If you decide to cancel the
								renewal, the recipient wil be given the opportunity to renew it
								themselves.
							</p>
							<p>
								Each renewal includes a donation to plant another{' '}
								{isEarthDay() ? (
									<>
										<span className="text-muted-foreground line-through">
											two
										</span>{' '}
										three trees
									</>
								) : (
									'two trees'
								)}
								.
							</p>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem
						data-vaul-no-drag
						value="item-9"
						className="border-b-0"
					>
						<AccordionTrigger data-vaul-no-drag>
							<h3 data-vaul-no-drag className="text-sm">
								How does Erda plant trees?
							</h3>
						</AccordionTrigger>
						<AccordionContent className="flex flex-col gap-2">
							<p>
								With each ownership and renewal, we donate to Trees for the
								Future (
								<a
									href="https://trees.org"
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-1 underline"
								>
									https://trees.org
								</a>
								) to plant{' '}
								{isEarthDay() ? (
									<>
										<span className="text-muted-foreground line-through">
											two
										</span>{' '}
										three trees
									</>
								) : (
									'two trees'
								)}{' '}
								in developing communities. This donation helps break the cycle
								of hunger, poverty, and environmental destruction.
							</p>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
			<div className="sticky -bottom-10 flex w-full flex-col  items-center justify-center gap-2 border-t bg-background pb-10 pt-3">
				<div className="flex gap-2">
					<Button data-vaul-no-drag variant="link" asChild>
						<Link to="/info">Info</Link>
					</Button>
					&bull;
					<Button data-vaul-no-drag variant="link" asChild>
						<Link to="/terms">Terms</Link>
					</Button>
					&bull;
					<Button data-vaul-no-drag variant="link" asChild>
						<Link to="/privacy">Privacy</Link>
					</Button>
					&bull;
					<Button data-vaul-no-drag variant="link" asChild>
						<Link to="/contact">Contact</Link>
					</Button>
				</div>
			</div>
		</>
	)
}
